import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { useIdentity } from "@/context/SiteConfigContext";
import { REQUIRES_CONFIRMATION } from "@/lib/constants";

const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [requestId, setRequestId] = useState(null);
  const attempts = useRef(0);
  const identity = useIdentity();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setRequestId(res.data.request_id);
        if (res.data.payment_status === "paid") {
          setState("paid");
          try {
            const p = JSON.parse(localStorage.getItem("uni_pending_purchase") || "null");
            if (p && p.session_id === sessionId) {
              trackEvent("purchase", { transaction_id: sessionId, amount: p.amount, currency: p.currency || "usd" });
              localStorage.removeItem("uni_pending_purchase");
            }
          } catch { /* noop */ }
          return;
        }
        if (["expired", "failed"].includes(res.data.status)) {
          setState("error");
          return;
        }
      } catch {
        /* keep trying */
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen grain grid place-items-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center card-soft"
        data-testid="payment-success"
      >
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-orange-700 animate-spin" />
            <h1 className="mt-6 font-heading text-2xl tracking-tight">{t("checkout.checking")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("common.loading")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-orange-600" />
            <h1 className="mt-6 font-heading text-3xl tracking-tight">{t("checkout.successTitle")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("checkout.successBody")}
            </p>
            <div className="mt-8 flex flex-col gap-2">
              {requestId && (
                <Button
                  className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => navigate(`/requests/${requestId}`)}
                  data-testid="success-view-request"
                >
                  {t("common.viewDetails")} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="success-dashboard">
                {t("checkout.goToDashboard")}
              </Button>
            </div>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-amber-500" />
            <h1 className="mt-6 font-heading text-2xl tracking-tight">
              {state === "timeout" ? t("checkout.successTitle") : t("forms.errorGeneric")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {state === "timeout"
                ? t("checkout.pending")
                : t("checkout.pending")}
            </p>
            <Button className="mt-8 rounded-sm" onClick={() => navigate("/dashboard")} data-testid="success-fallback-dashboard">
              {t("checkout.goToDashboard")}
            </Button>
          </>
        )}
        <p className="mt-8 pt-5 border-t border-border text-[11px] leading-relaxed text-muted-foreground/80" data-testid="payment-success-legal-line">
          Billed by {identity.legalName}{identity.regNumber !== REQUIRES_CONFIRMATION ? ` · Reg No. ${identity.regNumber}` : ""} · {identity.office}
        </p>
      </motion.div>
    </div>
  );
}
